<script setup lang="ts">
import { TextSelection } from '@tiptap/pm/state'
import type { Editor } from '@tiptap/vue-3'
import { BubbleMenu } from '@tiptap/vue-3'
import { computed, ref } from 'vue'
import LinkEditBlock from '@/notebook/extensions/Link/components/LinkEditBlock.vue'
import LinkViewBlock from '@/notebook/extensions/Link/components/LinkViewBlock.vue'

type Props = {
  editor: Editor
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const showEdit = ref(false)
const link = computed(() => {
  const { href: link } = props.editor.getAttributes('link')

  return link
})
const shouldShow: any = computed(() => {
  const isActive = props.editor.isActive('link')

  return isActive
})

function onSetLink(url: string, text?: string, openInNewTab?: boolean) {
  props.editor
    .chain()
    .extendMarkRange('link')
    .insertContent({
      type: 'text',
      text,
      marks: [
        {
          type: 'link',
          attrs: {
            href: url,
            target: openInNewTab ? '_blank' : '',
          },
        },
      ],
    })
    .setLink({ href: url })
    .focus()
    .run()
  showEdit.value = false
}
function unSetLink() {
  props.editor.chain().extendMarkRange('link').unsetLink().focus().run()
  showEdit.value = false
}
function onClickOutside() {
  const { state, view } = props.editor
  const { tr, selection } = state
  const transaction = tr.setSelection(TextSelection.create(state.doc, selection.from))

  view.dispatch(transaction)
  showEdit.value = false
}
</script>

<template>
  <BubbleMenu
    v-show="shouldShow"
    :editor="editor"
    :update-delay="0"
    :tippy-options="{
      popperOptions: {
        modifiers: [
          { name: 'flip', enabled: false },
          { name: 'preventOverflow', enabled: true },
        ],
        strategy: 'fixed',
      },
      appendTo: 'parent',
      placement: 'bottom-start',
      offset: [-2, 16],
      zIndex: 99999,
      onHidden: () => {
        showEdit = false
      },
    }"
  >
    <LinkEditBlock v-if="showEdit" :editor="editor" @on-set-link="onSetLink" @on-click-outside="onClickOutside" />
    <LinkViewBlock v-else :editor="editor" :link="link" @clear="unSetLink" @edit="showEdit = true" />
  </BubbleMenu>
</template>
